import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Home as HomeIcon, CalendarClock, ShieldCheck, MapPin, ArrowRight, Star } from "lucide-react";
import api from "@/lib/api";
import { IMG, SERVICE_AREA } from "@/lib/content";
import { PricingTable } from "@/components/Pricing";
import ReviewsSection from "@/components/ReviewsSection";
import InsuranceSection from "@/components/InsuranceSection";

const fade = { hidden: { opacity: 0, y: 24 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1 } }) };

export default function Home() {
  const [services, setServices] = useState([]);
  useEffect(() => { api.get("/services").then((r) => setServices(r.data)).catch(() => {}); }, []);
  const airbnb = services.find((s) => s.slug === "airbnb");

  return (
    <div>
      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={IMG.heroLiving} alt="Bright, immaculately cleaned living room" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 sm:py-40">
          <motion.div variants={fade} initial="hidden" animate="show" className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/90 bg-white/10 backdrop-blur px-4 py-2 rounded-full border border-white/20">
              <MapPin size={14} /> {SERVICE_AREA}
            </span>
            <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl text-white font-semibold leading-tight">
              Hospitality-grade cleaning for homes & short-term rentals
            </h1>
            <p className="mt-5 text-lg text-white/90 leading-relaxed max-w-xl">
              Masaya delivers warm, meticulous, hotel-standard cleaning so your home shines and your guests arrive to a flawless space—every time.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/book" className="bg-[#B87333] hover:bg-[#A06127] text-white font-medium rounded-full px-7 py-3.5 shadow-lg hover:shadow-xl active:scale-95 transition-all text-center inline-flex items-center justify-center gap-2" data-testid="hero-book-btn">
                Book Now <ArrowRight size={18} />
              </Link>
              <Link to="/get-estimate" className="bg-white/95 hover:bg-white text-[#2C2621] font-medium rounded-full px-7 py-3.5 shadow-md transition-all text-center" data-testid="hero-estimate-btn">
                Get an Estimate
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust badges */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-10">
        <div className="bg-white rounded-2xl border border-[#E6DEC3] shadow-[0_10px_40px_rgba(44,38,33,0.08)] grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-[#E6DEC3]/60">
          {[
            { icon: ShieldCheck, t: "Insured & reliable", d: "Consistent, trustworthy service" },
            { icon: Star, t: "Hotel standard", d: "Details others miss" },
            { icon: CalendarClock, t: "Easy online booking", d: "Real-time availability" },
            { icon: Sparkles, t: "Guest-ready turnovers", d: "Built for Airbnb hosts" },
          ].map((b, i) => (
            <div key={i} className="p-6 flex flex-col items-start gap-2">
              <b.icon className="text-[#B87333]" size={24} />
              <p className="font-semibold text-[#2C2621]">{b.t}</p>
              <p className="text-sm text-[#635B52]">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Services */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] text-[#B87333] font-semibold">What we do</p>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-[#2C2621]">Cleaning tailored to your space</h2>
        </div>
        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {[
            { img: IMG.turnoverBed, icon: Sparkles, title: "Airbnb Turnover", to: "/airbnb-turnover", desc: "Fast, dependable resets between guests with hotel-crisp presentation." },
            { img: IMG.residentialLiving, icon: HomeIcon, title: "Residential Cleaning", to: "/residential", desc: "Standard cleans that keep your home fresh and welcoming." },
            { img: IMG.deepKitchen, icon: Star, title: "Deep Cleaning", to: "/deep-cleaning", desc: "Detailed, top-to-bottom care. Estimate required." },
          ].map((c, i) => (
            <motion.div key={i} variants={fade} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="group bg-white rounded-2xl border border-[#E6DEC3] overflow-hidden shadow-[0_4px_20px_rgba(44,38,33,0.04)] hover:shadow-xl transition-all">
              <div className="h-52 overflow-hidden">
                <img src={c.img} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6">
                <c.icon className="text-[#B87333]" size={22} />
                <h3 className="mt-3 font-serif text-2xl text-[#2C2621]">{c.title}</h3>
                <p className="mt-2 text-[#635B52] leading-relaxed">{c.desc}</p>
                <Link to={c.to} className="mt-4 inline-flex items-center gap-1 text-[#B87333] font-medium hover:gap-2 transition-all" data-testid={`home-service-${i}`}>
                  Learn more <ArrowRight size={16} />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Airbnb pricing preview */}
      {airbnb && (
        <section className="bg-[#F5F0E8] py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#B87333] font-semibold">Transparent pricing</p>
                <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-[#2C2621]">Airbnb turnover rates</h2>
              </div>
              <Link to="/services" className="text-[#B87333] font-medium inline-flex items-center gap-1 hover:gap-2 transition-all">See all pricing <ArrowRight size={16} /></Link>
            </div>
            <PricingTable service={airbnb} />
          </div>
        </section>
      )}

      {/* Hospitality difference */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid md:grid-cols-2 gap-12 items-center">
        <div className="rounded-2xl overflow-hidden shadow-xl">
          <img src={IMG.towels} alt="Neatly folded fresh towels" className="w-full h-full object-cover" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#B87333] font-semibold">The Masaya difference</p>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-[#2C2621]">Warm, professional, and detail-obsessed</h2>
          <p className="mt-4 text-[#635B52] leading-relaxed">
            We treat every home like a boutique hotel. Crisp linens, spotless surfaces, and thoughtful finishing touches—delivered with genuine hospitality across {SERVICE_AREA}.
          </p>
          <div className="mt-8 flex gap-3">
            <Link to="/book" className="bg-[#B87333] hover:bg-[#A06127] text-white font-medium rounded-full px-6 py-3 shadow-md transition-all" data-testid="home-cta-book">Book Now</Link>
            <Link to="/contact" className="border border-[#B87333]/40 hover:border-[#B87333] text-[#2C2621] hover:bg-[#F5F0E8] font-medium rounded-full px-6 py-3 transition-all">Contact us</Link>
          </div>
        </div>
      </section>

      <ReviewsSection featured />

      <InsuranceSection />
    </div>
  );
}
